import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { TAB_CAPSULE_OVERHANG, TAB_DRAG_CATCH_UP_MS, catchUp, clampCapsule, nearestTab, swipeIntent, toBarX } from '../utils/tabBar'

/** The capsule's glide to a tab: a soft spring (a slight overshoot), on the compositor, so a heavy page never stalls it. */
export const TAB_GLIDE = '420ms cubic-bezier(.32,1.2,.5,1)'
const FADE = 'opacity 160ms ease'
/** Barely there: the capsule a hair larger while it's carried. */
export const TAB_DRAG_SWELL = 1.03
/** A drag can still end in a click (a finger that barely moved): how long one is swallowed. */
const CLICK_SWALLOW_MS = 400
/** A navigation never waits longer than this for a frame (a hidden tab has none). */
const PAINT_WAIT_MS = 100

// Layout px, never the screen's: the capsule lives inside the bar, which may be scaled. Read at rest and at a gesture's
// start only, sub-pixel (offsetLeft rounds, and five tabs share the width in fractions).
function measureBar(bar) {
  const box = bar.getBoundingClientRect()
  const scale = bar.offsetWidth > 0 ? box.width / bar.offsetWidth : 1
  const origin = box.left + bar.clientLeft * scale
  const tabs = Array.from(bar.querySelectorAll('[data-tab]'), (tab) => tab.getBoundingClientRect())
  return {
    origin,
    scale,
    centers: tabs.map((r) => toBarX(r.left + r.width / 2, origin, scale)),
    width: (tabs[0]?.width ?? 0) / scale + 2 * TAB_CAPSULE_OVERHANG,
  }
}

// Runs `then` right after the next paint: the glide reaches the compositor first, so the new page's render can't hold it
// back. Costs a frame at most. Returns the cancel.
function afterPaint(then) {
  let frame = 0
  let soon = 0
  const run = () => {
    cancel()
    then()
  }
  const wait = window.setTimeout(run, PAINT_WAIT_MS)
  const cancel = () => {
    cancelAnimationFrame(frame)
    window.clearTimeout(soon)
    window.clearTimeout(wait)
  }
  frame = requestAnimationFrame(() => {
    soon = window.setTimeout(run, 0)
  })
  return cancel
}

// The floating tab bar's capsule: it rests behind the current tab, glides to a tapped one, and can be dragged along the
// bar (from anywhere on it, so a page with no current tab has one too), the icon under it previewed as active; let go,
// it settles on the nearest tab and navigates there, unless that's the tab you're on.
//
// A frame touches only the capsule's transform and one attribute: no React render while the finger moves, and the tabs
// are measured once, when the gesture starts. The page's scroll is never in the way: the bar is `touch-action: pan-y`,
// its blocking touchmove listener lives on the bar alone, and it only ever refuses a touch that is already a drag. iOS
// needs that refusal: left alone, its scroll would take a drag that drifts down a little, and cancel the pointer.
export function useTabBarDrag({ keys, current, reduced, onSelect }) {
  const barRef = useRef(null)
  const capsuleRef = useRef(null)
  const latest = useRef({ keys, current, reduced, onSelect })
  latest.current = { keys, current, reduced, onSelect }
  const gesture = useRef(null)
  /** Where the capsule rests; null while it's hidden (no current tab). */
  const restingAt = useRef(null)
  const pendingSelect = useRef(null)
  const placed = useRef(false)

  const place = useCallback((x, swell, transition) => {
    const capsule = capsuleRef.current
    if (!capsule) return
    capsule.style.transition = transition
    capsule.style.transform = `translate3d(${x}px, 0, 0) scale(${swell})`
  }, [])

  // Puts the capsule on a tab; with no tab it fades out where it is, and comes back on the next one with no trip across.
  const rest = useCallback(
    (key, animate) => {
      const bar = barRef.current
      const capsule = capsuleRef.current
      if (!bar || !capsule) return
      const index = key === null ? -1 : latest.current.keys.indexOf(key)
      if (index < 0) {
        restingAt.current = null
        capsule.style.transition = latest.current.reduced ? 'none' : FADE
        capsule.dataset.shown = 'false'
        return
      }
      const wasShown = capsule.dataset.shown === 'true'
      const { centers, width } = measureBar(bar)
      const x = centers[index] - width / 2
      restingAt.current = x
      capsule.style.width = `${width}px`
      capsule.dataset.shown = 'true'
      const still = latest.current.reduced
      place(x, 1, still ? 'none' : animate && wasShown ? `transform ${TAB_GLIDE}, ${FADE}` : FADE)
    },
    [place],
  )

  /** Glide to a tab now and go there: the one way a tap and a drag navigate. */
  const select = useCallback(
    (key) => {
      barRef.current?.setAttribute('data-lit', key)
      rest(key, true)
      pendingSelect.current?.()
      pendingSelect.current = null
      if (latest.current.reduced) {
        latest.current.onSelect(key)
        return
      }
      pendingSelect.current = afterPaint(() => {
        pendingSelect.current = null
        latest.current.onSelect(key)
      })
    },
    [rest],
  )

  useLayoutEffect(() => {
    if (gesture.current?.dragging) return // the drag's end reads the new tab
    rest(current, placed.current)
    placed.current = true
  }, [current, rest])

  useEffect(() => {
    const bar = barRef.current
    if (!bar || typeof ResizeObserver === 'undefined') return
    // A rotation, or the bar leaving display: none. Its shrink is a transform and doesn't reach this.
    const observer = new ResizeObserver(() => {
      if (!gesture.current) rest(latest.current.current, false)
    })
    observer.observe(bar)
    return () => observer.disconnect()
  }, [rest])

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    let frame = 0
    let stopSwallowing = null

    const targetOf = (g) => clampCapsule(toBarX(g.clientX, g.metrics.origin, g.metrics.scale) - g.grab, g.metrics.centers, g.metrics.width)

    const paint = () => {
      frame = 0
      const g = gesture.current
      if (!g?.dragging) return
      const x = targetOf(g)
      const elapsed = performance.now() - g.dragAt
      place(x + catchUp(g.gap, elapsed), latest.current.reduced ? 1 : TAB_DRAG_SWELL, 'none')
      const lit = latest.current.keys[nearestTab(g.metrics.centers, x + g.metrics.width / 2)]
      if (lit !== g.lit) {
        g.lit = lit
        bar.setAttribute('data-lit', lit)
      }
      if (elapsed < TAB_DRAG_CATCH_UP_MS) frame = requestAnimationFrame(paint)
    }

    const startDrag = (g) => {
      g.dragging = true
      g.dragAt = performance.now()
      const capsule = capsuleRef.current
      if (capsule) {
        capsule.style.width = `${g.metrics.width}px`
        capsule.dataset.shown = 'true'
      }
      // Hidden, it just appears under the finger; shown, it travels there instead of jumping (the slop, or from another tab).
      g.gap = restingAt.current === null || latest.current.reduced ? 0 : restingAt.current - targetOf(g)
      try {
        bar.setPointerCapture?.(g.pointerId)
      } catch {
        // The pointer is already gone: its pointercancel ends this.
      }
    }

    const swallowClick = () => {
      const swallow = (e) => {
        e.preventDefault()
        e.stopPropagation()
        stop()
      }
      const stop = () => {
        window.removeEventListener('click', swallow, true)
        window.clearTimeout(timer)
        stopSwallowing = null
      }
      window.addEventListener('click', swallow, true)
      const timer = window.setTimeout(stop, CLICK_SWALLOW_MS)
      stopSwallowing = stop
    }

    const onMove = (e) => {
      const g = gesture.current
      if (!g || e.pointerId !== g.pointerId) return
      g.clientX = e.clientX
      if (!g.dragging) {
        const intent = swipeIntent(e.clientX - g.startX, e.clientY - g.startY)
        if (intent === 'scroll') finish(false)
        if (intent !== 'drag') return
        startDrag(g)
      }
      if (!frame) frame = requestAnimationFrame(paint)
    }
    // Never its clientX: Mobile Safari's pointerup reports 0 (seen on iOS 26.4). The last move is where the finger ended.
    const onUp = (e) => {
      if (e.pointerId === gesture.current?.pointerId) finish(true)
    }
    const onCancel = (e) => {
      if (e.pointerId === gesture.current?.pointerId) finish(false)
    }
    // Only the bar's own capture: taking the finger over from the tab fires this on the tab, and it bubbles here.
    const onCaptureLost = (e) => {
      if (e.target === bar) finish(false)
    }

    const stopTracking = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      bar.removeEventListener('lostpointercapture', onCaptureLost)
      if (frame) cancelAnimationFrame(frame)
      frame = 0
    }

    const finish = (commit) => {
      const g = gesture.current
      if (!g) return
      gesture.current = null
      stopTracking()
      if (!g.dragging) return // a tap (its click comes next) or the page's scroll: nothing moved
      swallowClick()
      const { keys: tabs, current: here } = latest.current
      const drop = tabs[nearestTab(g.metrics.centers, targetOf(g) + g.metrics.width / 2)]
      if (commit && drop !== here) {
        select(drop)
        return
      }
      bar.setAttribute('data-lit', here ?? '')
      rest(here, true)
    }

    const onDown = (e) => {
      if (gesture.current || e.isPrimary === false || e.button !== 0 || !latest.current.keys.length) return
      stopSwallowing?.()
      const metrics = measureBar(bar)
      const finger = toBarX(e.clientX, metrics.origin, metrics.scale)
      const left = restingAt.current
      const onCapsule = left !== null && finger >= left && finger <= left + metrics.width
      gesture.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        clientX: e.clientX,
        metrics,
        grab: onCapsule ? finger - left : metrics.width / 2,
        dragging: false,
        dragAt: 0,
        gap: 0,
        lit: latest.current.current ?? '',
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onCancel)
      bar.addEventListener('lostpointercapture', onCaptureLost)
    }
    const onTouchMove = (e) => {
      if (gesture.current?.dragging && e.cancelable) e.preventDefault()
    }
    // A long press on a link would open the phone's menu and cancel the pointer.
    const onContextMenu = (e) => {
      if (gesture.current) e.preventDefault()
    }

    bar.addEventListener('pointerdown', onDown)
    bar.addEventListener('touchmove', onTouchMove, { passive: false })
    bar.addEventListener('contextmenu', onContextMenu)
    return () => {
      bar.removeEventListener('pointerdown', onDown)
      bar.removeEventListener('touchmove', onTouchMove)
      bar.removeEventListener('contextmenu', onContextMenu)
      gesture.current = null
      stopTracking()
      stopSwallowing?.()
      pendingSelect.current?.()
      pendingSelect.current = null
    }
  }, [place, rest, select])

  return { barRef, capsuleRef, select }
}
